"use client";

// TipTap "@" artifact-mention extension. Ported from the private
// `@magnus/react` editor; the "Manage artifacts" footer link became the
// `options.manage` seam (absent → hidden) so this file can be published.
// Single source for npm + registry + (via shims) `@magnus/react`.
import { c as _c } from "react/compiler-runtime";
import { nodeInputRule, nodePasteRule } from "@tiptap/core";
import Mention, { type MentionNodeAttrs } from "@tiptap/extension-mention";
import { PluginKey } from "@tiptap/pm/state";
import { type NodeViewProps, NodeViewWrapper, ReactNodeViewRenderer, ReactRenderer } from "@tiptap/react";
import type { SuggestionKeyDownProps, SuggestionProps } from "@tiptap/suggestion";
import { cn } from "@workspace/ui/lib/utils";
import { AppWindowIcon, SlidersHorizontalIcon } from "lucide-react";
import { type Ref, type RefObject, useEffect, useImperativeHandle, useRef, useState } from "react";
import tippy, { type Instance as TippyInstance } from "tippy.js";
import { MessageArtifactReference } from "./chips";
import { ARTIFACT_REF_RE, type ArtifactPickerItem } from "./refs";
import { type MentionManageLink, suggestionClientRect } from "./skill-mention";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ArtifactMentionAttrs = MentionNodeAttrs & {
  slug?: string;
};
export interface ArtifactMentionOptions {
  /** "Manage artifacts" footer link. Hidden when absent. */
  manage?: MentionManageLink;
}
export type ActiveArtifactSuggestion = {
  component: ReactRenderer<SuggestionListHandle>;
  query: string;
  command: (attrs: ArtifactMentionAttrs) => void;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function toArtifactMentionAttrs(item: ArtifactPickerItem): ArtifactMentionAttrs {
  return {
    id: item.artifactId,
    label: item.artifactName,
    slug: item.artifactSlug
  };
}

// ---------------------------------------------------------------------------
// Inline chip rendered inside the editor
// ---------------------------------------------------------------------------

function ArtifactChipView(t0) {
  const $ = _c(4);
  const {
    node,
    deleteNode,
    editor
  } = t0;
  const attrs = node.attrs as ArtifactMentionAttrs;
  const removable = editor.isEditable;
  const t1 = attrs.label ?? "";
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <AppWindowIcon />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const t3 = removable ? deleteNode : undefined;
  let t4;
  if ($[1] !== t1 || $[2] !== t3) {
    t4 = <NodeViewWrapper as="span" className="inline"><MessageArtifactReference artifactName={t1} className="mx-0.5 inline-flex align-middle" icon={t2} onRemove={t3} /></NodeViewWrapper>;
    $[1] = t1;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  return t4;
}

// ---------------------------------------------------------------------------
// Suggestion dropdown
// ---------------------------------------------------------------------------

type SuggestionListProps = {
  items: ArtifactPickerItem[];
  isLoading?: boolean;
  command: (attrs: ArtifactMentionAttrs) => void;
  manage?: MentionManageLink;
};
type SuggestionListHandle = {
  onKeyDown: (props: {
    event: KeyboardEvent;
  }) => boolean;
};
function SuggestionList(t0) {
  const $ = _c(21);
  const {
    ref,
    items,
    isLoading,
    command,
    manage
  } = t0;
  const [selectedIndex, setSelectedIndex] = useState(0);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const itemRefs = useRef(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setSelectedIndex(0);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== items) {
    t3 = [items];
    $[2] = items;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[4] !== selectedIndex) {
    t4 = () => {
      const el = itemRefs.current[selectedIndex];
      if (!el) {
        return;
      }
      const container = el.parentElement;
      if (!container) {
        return;
      }
      const itemRect = el.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      if (itemRect.top < containerRect.top + 32) {
        container.scrollTop = container.scrollTop - (containerRect.top + 32 - itemRect.top);
      } else {
        if (itemRect.bottom > containerRect.bottom - 32) {
          container.scrollTop = container.scrollTop + (itemRect.bottom - (containerRect.bottom - 32));
        }
      }
    };
    t5 = [selectedIndex];
    $[4] = selectedIndex;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  if ($[7] !== command || $[8] !== items || $[9] !== selectedIndex) {
    t6 = () => ({
      onKeyDown: t7 => {
        const {
          event
        } = t7;
        if (items.length === 0) {
          return false;
        }
        if (event.key === "ArrowUp") {
          setSelectedIndex(i => (i + items.length - 1) % items.length);
          return true;
        }
        if (event.key === "ArrowDown") {
          setSelectedIndex(i_0 => (i_0 + 1) % items.length);
          return true;
        }
        if (event.key === "Enter" || event.key === "Tab") {
          const item = items[selectedIndex];
          if (item) {
            command(toArtifactMentionAttrs(item));
          }
          return true;
        }
        if (event.key === "Escape") {
          return true;
        }
        return false;
      }
    });
    $[7] = command;
    $[8] = items;
    $[9] = selectedIndex;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  useImperativeHandle(ref, t6);
  let t7;
  if ($[11] !== command || $[12] !== isLoading || $[13] !== items || $[14] !== selectedIndex) {
    t7 = items.length > 0 ? <div className="max-h-72 overflow-y-auto overscroll-contain p-1 [scrollbar-width:thin]">{items.map((item_0, index) => <button className={cn("flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors", index === selectedIndex ? "bg-accent text-accent-foreground" : "hover:bg-accent/50")} key={item_0.artifactId} onClick={() => command(toArtifactMentionAttrs(item_0))} ref={el_0 => {
        itemRefs.current[index] = el_0;
      }} type="button"><AppWindowIcon className="size-4 shrink-0 text-muted-foreground" /><span className="truncate">{item_0.artifactName}</span></button>)}</div> : <div className="p-2"><p className="px-1 text-muted-foreground text-xs">{isLoading ? "Loading artifacts\u2026" : "No artifacts found"}</p></div>;
    $[11] = command;
    $[12] = isLoading;
    $[13] = items;
    $[14] = selectedIndex;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== manage) {
    t8 = manage && <div className="border-t p-1"><a className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-muted-foreground transition-colors hover:bg-accent/50 hover:text-accent-foreground" href={manage.href} onClick={e => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) {
          return;
        }
        e.preventDefault();
        (manage.navigate ?? _temp)(manage.href);
      }}><SlidersHorizontalIcon className="size-4 shrink-0" />{manage.label}</a></div>;
    $[16] = manage;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t7 || $[19] !== t8) {
    t9 = <div className="min-w-[240px] max-w-[320px] rounded-lg border bg-popover shadow-md">{t7}{t8}</div>;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
}

// ---------------------------------------------------------------------------
// Suggestion renderer factory (tippy.js — portals to body for z-index)
// ---------------------------------------------------------------------------
function _temp(href) {
  return location.assign(href);
}
function createSuggestionRenderer(artifactsRef: RefObject<ArtifactPickerItem[]>, loadingRef: RefObject<boolean>, activeSuggestionRef: RefObject<ActiveArtifactSuggestion | null>, optionsRef: RefObject<ArtifactMentionOptions | undefined>, placement: "top-start" | "bottom-start") {
  return {
    char: "@",
    // Only trigger at a word boundary so mid-word `@` (email addresses,
    // handles) doesn't pop the picker — matches the skill mention rule.
    allowedPrefixes: [" "],
    pluginKey: new PluginKey("artifactMentionSuggestion"),
    items: ({
      query
    }: {
      query: string;
    }) => {
      const q = query.toLowerCase();
      const filtered = (artifactsRef.current ?? []).filter(a => a.artifactName.toLowerCase().includes(q));
      return filtered.slice(0, 50);
    },
    render: () => {
      let component: ReactRenderer<SuggestionListHandle> | null = null;
      let tippyInstance: TippyInstance | null = null;
      return {
        onStart: (props: SuggestionProps<ArtifactPickerItem>) => {
          component = new ReactRenderer(SuggestionList, {
            props: {
              items: props.items,
              isLoading: loadingRef.current,
              command: props.command,
              manage: optionsRef.current?.manage
            },
            editor: props.editor
          });
          activeSuggestionRef.current = {
            component,
            query: props.query,
            command: props.command
          };
          const getRect = suggestionClientRect(props.clientRect);
          if (!getRect) {
            return;
          }
          const [instance] = tippy("body", {
            getReferenceClientRect: getRect,
            appendTo: () => document.body,
            content: component.element,
            showOnCreate: true,
            interactive: true,
            trigger: "manual",
            placement,
            maxWidth: "none"
          });
          tippyInstance = instance ?? null;
        },
        onUpdate: (props: SuggestionProps<ArtifactPickerItem>) => {
          component?.updateProps({
            items: props.items,
            isLoading: loadingRef.current,
            command: props.command,
            manage: optionsRef.current?.manage
          });
          if (activeSuggestionRef.current) {
            activeSuggestionRef.current.query = props.query;
            activeSuggestionRef.current.command = props.command;
          }
          const getRect = suggestionClientRect(props.clientRect);
          if (getRect) {
            tippyInstance?.setProps({
              getReferenceClientRect: getRect
            });
          }
        },
        onKeyDown: (props: SuggestionKeyDownProps) => {
          if (props.event.key === "Escape") {
            tippyInstance?.hide();
            return true;
          }
          return component?.ref?.onKeyDown(props) ?? false;
        },
        onExit: () => {
          activeSuggestionRef.current = null;
          tippyInstance?.destroy();
          tippyInstance = null;
          component?.destroy();
          component = null;
        }
      };
    }
  };
}

// ---------------------------------------------------------------------------
// Hook — creates a fully configured artifact mention extension
// ---------------------------------------------------------------------------

export function useArtifactMention(availableArtifacts: ArtifactPickerItem[], artifactsLoading: boolean, placement: "top-start" | "bottom-start" = "bottom-start", options?: ArtifactMentionOptions) {
  const artifactsRef = useRef<ArtifactPickerItem[]>(availableArtifacts);
  artifactsRef.current = availableArtifacts;
  const loadingRef = useRef(artifactsLoading);
  loadingRef.current = artifactsLoading;
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const activeSuggestionRef = useRef<ActiveArtifactSuggestion | null>(null);
  const extension = useRef(Mention.extend({
    // Distinct node name so the artifact and skill mention extensions can
    // coexist in the same editor without clobbering each other's schema.
    name: "artifactMention",
    addAttributes() {
      return {
        ...this.parent?.(),
        slug: {
          default: null,
          parseHTML: (el: HTMLElement) => el.getAttribute("data-slug")
        }
      };
    },
    addNodeView() {
      return ReactNodeViewRenderer(ArtifactChipView);
    },
    // Typing or pasting `{{artifact:id|name}}` converts to a chip using the
    // shared ARTIFACT_REF_RE, so there's one canonical format everywhere.
    addInputRules() {
      return [nodeInputRule({
        find: new RegExp(`${ARTIFACT_REF_RE.source}$`),
        type: this.type,
        getAttributes: match => ({
          id: match[1] ?? "",
          label: match[2] ?? ""
        })
      })];
    },
    addPasteRules() {
      return [nodePasteRule({
        find: new RegExp(ARTIFACT_REF_RE.source, "g"),
        type: this.type,
        getAttributes: match_0 => ({
          id: match_0[1] ?? "",
          label: match_0[2] ?? ""
        })
      })];
    }
  }).configure({
    deleteTriggerWithBackspace: true,
    HTMLAttributes: {
      class: "artifact-mention"
    },
    suggestion: createSuggestionRenderer(artifactsRef, loadingRef, activeSuggestionRef, optionsRef, placement)
  })).current;
  useEffect(() => {
    const active = activeSuggestionRef.current;
    if (!active) {
      return;
    }
    const q = active.query.toLowerCase();
    const items = availableArtifacts.filter(a => a.artifactName.toLowerCase().includes(q)).slice(0, 8);
    active.component.updateProps({
      items,
      isLoading: artifactsLoading,
      command: active.command,
      manage: optionsRef.current?.manage
    });
  }, [availableArtifacts, artifactsLoading]);
  return {
    extension,
    activeSuggestionRef
  };
}